import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Eye, EyeOff } from 'lucide-react';
import { useAuth } from '../../hooks/useAuth';
import logoImg from '../../assets/logo.png';
import templeImg from '../../assets/temple-far1.jpg';
import './AuthTheme.css';

const API = process.env.REACT_APP_API_URL || 'https://civilfox-backend.onrender.com/api';

function useTimer() {
  const [t, setT] = useState(0);
  useEffect(() => {
    if (t <= 0) return;
    const id = setTimeout(() => setT(n => n - 1), 1000);
    return () => clearTimeout(id);
  }, [t]);
  return [t, setT];
}

const post = async (path, body) => {
  const res = await fetch(`${API}/auth/${path}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  return res.json();
};

export default function RegisterPage() {
  const { loginWithData } = useAuth();
  const navigate = useNavigate();

  const [step, setStep] = useState(1);            // 1 details, 2 code
  const [form, setForm] = useState({ name: '', email: '', password: '' });
  const [otp, setOtp] = useState('');
  const [msg, setMsg] = useState(null);
  const [busy, setBusy] = useState(false);
  const [show, setShow] = useState(false);
  const [resend, setResend] = useTimer();

  const say = (type, text) => setMsg({ type, text });

  /* Backend still expects these fields, so they are sent with defaults */
  const payload = () => ({ ...form, phone: '', targetYear: new Date().getFullYear() + 1, preferredSubjects: [] });

  const sendOtp = async e => {
    e.preventDefault(); setMsg(null);
    if (form.password.length < 8) { say('err', 'Use at least 8 characters.'); return; }
    setBusy(true);
    try {
      const d = await post('send-otp', payload());
      if (!d.success) { say('err', d.message); return; }
      say('ok', `Code sent to ${form.email}`); setStep(2); setResend(60);
    } catch { say('err', 'Network error.'); }
    finally { setBusy(false); }
  };

  const verify = async e => {
    e.preventDefault(); setMsg(null); setBusy(true);
    try {
      const d = await post('verify-otp', { email: form.email, otp });
      if (!d.success) { say('err', d.message); return; }
      loginWithData(d.token, d.user);
      navigate('/dashboard');
    } catch { say('err', 'Network error.'); }
    finally { setBusy(false); }
  };

  const resendOtp = async () => {
    setMsg(null);
    try {
      const d = await post('resend-otp', { email: form.email });
      if (d.success) { say('ok', 'Code sent.'); setResend(60); } else say('err', d.message);
    } catch { say('err', 'Network error.'); }
  };

  return (
    <div className="au">
      <div className="au-card">
        <div className="au-art"><img src={templeImg} alt="" /></div>

        <div className="au-form">
          <img className="au-logo" src={logoImg} alt="CivilFox" />

          {msg && <p className={`au-msg ${msg.type}`} role="alert">{msg.text}</p>}

          {step === 1 && (
            <form className="au-fields" onSubmit={sendOtp}>
              <input className="au-in" type="text" placeholder="Name" aria-label="Name" autoComplete="name" required
                value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} />
              <input className="au-in" type="email" placeholder="Email" aria-label="Email" autoComplete="email" required
                value={form.email} onChange={e => setForm(f => ({ ...f, email: e.target.value }))} />
              <div className="au-wrap">
                <input className="au-in" type={show ? 'text' : 'password'} placeholder="Password" aria-label="Password" autoComplete="new-password" required
                  value={form.password} onChange={e => setForm(f => ({ ...f, password: e.target.value }))} />
                <button type="button" className="au-eye" onClick={() => setShow(s => !s)} aria-label={show ? 'Hide password' : 'Show password'}>
                  {show ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
              <button className="au-btn" type="submit" disabled={busy}>{busy ? 'Sending…' : 'Continue'}</button>
            </form>
          )}

          {step === 2 && (
            <form className="au-fields" onSubmit={verify}>
              <input className="au-in au-otp" inputMode="numeric" maxLength={6} placeholder="000000" aria-label="Code" required autoFocus
                value={otp} onChange={e => setOtp(e.target.value.replace(/\D/g, ''))} />
              <button className="au-btn" type="submit" disabled={busy || otp.length !== 6}>{busy ? 'Verifying…' : 'Verify'}</button>
              <div className="au-links">
                <button type="button" className="au-link" disabled={resend > 0} onClick={resendOtp}>
                  {resend > 0 ? `Resend in ${resend}s` : 'Resend'}
                </button>
                <button type="button" className="au-link" onClick={() => { setStep(1); setOtp(''); setMsg(null); }}>Change email</button>
              </div>
            </form>
          )}

          <div className="au-links">
            <Link to="/login" className="au-link">Sign in</Link>
          </div>
        </div>
      </div>
    </div>
  );
}